import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Box, Menu, TextField, InputAdornment, Button } from '@mui/material';
import DateRangeIcon from "@mui/icons-material/DateRange";
import ClearIcon from "@mui/icons-material/Clear";
import moment from "moment";
import './styles.scss';

export const DateRange = forwardRef((props, ref) => {
    const { startDate, endDate, handleChange, label, hideClearIcon, minDate, maxDate } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [state, setState] = useState([
        {
            startDate: startDate ? new Date(startDate) : new Date(),
            endDate: endDate ? new Date(endDate) : new Date(),
            key: 'selection'
        }
    ]);
    const [textValue, setTextValue] = useState('');

    useEffect(() => {
        if (startDate && endDate) {
            setState([{
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                key: 'selection'
            }]);
            setTextValue(`${moment(startDate).format('DD-MM-YYYY')} - ${moment(endDate).format('DD-MM-YYYY')}`);
        } else {
            setTextValue('');
        }
    }, [startDate, endDate]);

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const onSelect = (item) => {
        setState([item.selection]);
        // Only trigger change and close if a range is fully selected (start != end or we force it)
        // react-date-range updates selection immediately. If start == end, they just clicked the first date.
        // If they click same date twice, start == end, but we might want to allow 1-day ranges.
        // We can just update text value immediately and let them close manually, or close automatically.
        const startStr = moment(item.selection.startDate).format('YYYY-MM-DD');
        const endStr = moment(item.selection.endDate).format('YYYY-MM-DD');
        handleChange({ start: startStr, end: endStr });
        
        // If it's a date range selection and not just the first click
        if (item.selection.startDate !== item.selection.endDate) {
            handleClose();
        }
    };

    const handleClear = () => {
        setState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
        setTextValue('');
        handleChange({});
        handleClose();
    };

    useImperativeHandle(ref, () => ({ handleClear }));

    return (
        <div className=''>
            <Box>
                <TextField
                    fullWidth
                    label={label || 'Date Range'}
                    value={textValue}
                    variant="outlined"
                    size="small"
                    onClick={handleClick}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                {textValue && !hideClearIcon && (
                                    <Box onClick={(e) => { e.stopPropagation(); handleClear(); }} sx={{ cursor: 'pointer', mr: 1, display: 'flex' }}>
                                        <ClearIcon fontSize="small" />
                                    </Box>
                                )}
                                <Box sx={{ cursor: 'pointer', display: 'flex' }}>
                                    <DateRangeIcon fontSize="small" />
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{ style: { padding: 0 } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Box p={1}>
                    <DateRangePicker
                        onChange={onSelect}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={2}
                        ranges={state}
                        direction="horizontal"
                        minDate={minDate ? new Date(minDate) : undefined}
                        maxDate={maxDate ? new Date(maxDate) : undefined}
                    />
                    <Box display="flex" justifyContent="flex-end" p={1}>
                        <Button onClick={handleClose} variant="contained" size="small">Done</Button>
                    </Box>
                </Box>
            </Menu>
        </div>
    );
});